import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2, AlertCircle, MapPin, Calendar, Hash, Package,
  Phone, Mail, ExternalLink, ShieldCheck, Leaf, Flame, Snowflake, BookOpen,
} from "lucide-react";

/**
 * Page PUBLIQUE de traçabilité Maison Thaï.
 *
 * Accédée via le QR code imprimé sur chaque étiquette barquette :
 *    https://maison-thai.ch/trace/{sku}/{lot_number}
 *
 * Confidentialité (Phase 86 / 86c) :
 *  - Pas de Google Fonts ni tracker tiers sur cette route (RGPD)
 *  - Liste INCO de composition affichée (déjà imprimée sur la barquette,
 *    obligation légale UE n°1169/2011)
 *  - Allergènes, valeurs nutritionnelles, conservation, réchauffage affichés
 *  - PAS de recette interne (proportions, étapes HACCP, équipements)
 *  - PAS de résultats de tests laboratoire
 *  - meta robots noindex pour ne pas polluer Google
 *
 * Mobile-first, polices système uniquement, ultra-léger.
 */
const PUBLIC_API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || "";

// Stack de polices système 100% RGPD-safe (aucune requête réseau)
const SYSTEM_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export default function PublicTracePage() {
  const { sku, lot_number: lotNumber } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pose une meta robots=noindex,nofollow sur cette page UNIQUEMENT (et nettoie au démontage)
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Traçabilité produit — Maison Thaï";
    return () => {
      meta.remove();
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    const fetchTrace = async () => {
      try {
        const url = lotNumber
          ? `${PUBLIC_API_BASE}/api/shop/trace/${encodeURIComponent(sku)}/${encodeURIComponent(lotNumber)}`
          : `${PUBLIC_API_BASE}/api/shop/trace/${encodeURIComponent(sku)}`;
        const r = await fetch(url);
        if (!r.ok) throw new Error(r.status === 404 ? "Produit ou lot introuvable" : `Erreur HTTP ${r.status}`);
        const j = await r.json();
        setData(j);
      } catch (e) {
        setError(e.message || "Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };
    fetchTrace();
  }, [sku, lotNumber]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#FBFAF6]"
        style={{ fontFamily: SYSTEM_FONT_STACK }}
        data-testid="trace-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin text-[#1F4E3D] mr-2" />
        <span className="text-sm text-[#575A57]">Chargement…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#FBFAF6] p-6"
        style={{ fontFamily: SYSTEM_FONT_STACK }}
        data-testid="trace-error"
      >
        <div className="max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-[#C05C47] mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-[#1A1C1A] mb-1">Information indisponible</h1>
          <p className="text-sm text-[#575A57] mb-4">{error}</p>
          <a
            href="https://maison-thai.ch"
            className="inline-block text-sm text-[#1F4E3D] underline"
          >
            Retour au site Maison Thaï
          </a>
        </div>
      </div>
    );
  }

  const { product, lot, distributor, support } = data;
  const photo = product.image_url || product.photo_packaging_url || "";

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
      return iso.slice(0, 10);
    }
  };

  // Statut DLC pour mise en évidence (verte / orange / rouge)
  let dlcTone = "neutral";
  if (lot?.dlc) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dlcDate = new Date(lot.dlc);
    const diffDays = Math.ceil((dlcDate - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) dlcTone = "expired";
    else if (diffDays <= 2) dlcTone = "soon";
    else dlcTone = "ok";
  }
  const dlcStyles = {
    ok:      "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]",
    soon:    "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]",
    expired: "bg-[#FBEEEA] text-[#A04E2C] border-[#E8B0A0]",
    neutral: "bg-[#F4F4F0] text-[#575A57] border-[#E5E5E0]",
  };

  return (
    <div
      className="min-h-screen bg-[#FBFAF6]"
      style={{ fontFamily: SYSTEM_FONT_STACK }}
      data-testid="trace-page"
    >
      {/* Header brand */}
      <header className="bg-[#1F4E3D] text-white px-5 py-4">
        <a href="https://maison-thai.ch" className="block">
          <p className="text-xs uppercase tracking-widest text-[#D4A373] font-medium">Maison Thaï</p>
          <h1 className="text-xl font-semibold">Traçabilité produit</h1>
        </a>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 space-y-5">
        {/* Carte produit */}
        <section className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden" data-testid="trace-product">
          {photo && (
            <img
              src={photo}
              alt={product.name}
              className="w-full h-48 sm:h-56 object-cover"
              loading="lazy"
            />
          )}
          <div className="p-5">
            <p className="text-[10px] uppercase tracking-wider text-[#8C6B2F] font-semibold mb-1">
              {product.category || "Plat préparé"}
            </p>
            <h2 className="text-2xl text-[#1A1C1A] font-semibold leading-tight">
              {product.name}
            </h2>
            {product.description && (
              <p className="mt-2 text-sm text-[#575A57] leading-relaxed">
                {product.description}
              </p>
            )}
            <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-[#575A57]">
              {product.net_weight && (
                <span className="inline-flex items-center gap-1">
                  <Package className="w-3 h-3" />
                  {product.net_weight} {product.net_weight_unit || "g"}
                  {product.net_weight_detail && (
                    <span className="text-[#A3A6A3]">· {product.net_weight_detail}</span>
                  )}
                </span>
              )}
              {product.ean13_base && (
                <span className="inline-flex items-center gap-1 font-mono text-[10px]">
                  EAN {product.ean13_base}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Bloc Lot + DLC — affiché uniquement si un lot est résolu */}
        {lot ? (
          <section className="bg-white border border-[#E5E5E0] rounded-lg p-5" data-testid="trace-lot">
            <h3 className="text-lg font-semibold text-[#1A1C1A] mb-3 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#1F4E3D]" />
              Informations du lot
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <InfoRow icon={Hash} label="N° de lot" value={lot.lot_number} />
              {lot.production_date && (
                <InfoRow icon={Calendar} label="Date de production" value={formatDate(lot.production_date)} />
              )}
              {lot.dlc && (
                <div className="col-span-1 sm:col-span-2">
                  <p className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold mb-1">
                    Date limite de consommation (DLC)
                  </p>
                  <div className={`inline-block px-3 py-1.5 rounded border text-sm font-medium ${dlcStyles[dlcTone]}`} data-testid="trace-dlc">
                    {formatDate(lot.dlc)}
                    {dlcTone === "soon" && <span className="ml-1 text-[10px]">· à consommer rapidement</span>}
                    {dlcTone === "expired" && <span className="ml-1 text-[10px]">· dépassée</span>}
                  </div>
                </div>
              )}
              {lot.dluo && (
                <InfoRow icon={Calendar} label="DLUO" value={formatDate(lot.dluo)} />
              )}
            </div>
          </section>
        ) : (
          <section className="bg-[#FBF4E9] border border-[#E8D3A8] rounded-lg p-4 text-xs text-[#8C6B2F]" data-testid="trace-no-lot">
            Lot non précisé sur ce QR. Les informations de DLC sont indiquées directement sur l&apos;étiquette de votre barquette.
          </section>
        )}

        {/* Distributeur d'origine */}
        {distributor && (
          <section className="bg-white border border-[#E5E5E0] rounded-lg p-5" data-testid="trace-distributor">
            <h3 className="text-lg font-semibold text-[#1A1C1A] mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#1F4E3D]" />
              Distributeur d&apos;origine
            </h3>
            <p className="font-medium text-[#1A1C1A]">{distributor.name}</p>
            {distributor.address && <p className="text-sm text-[#575A57]">{distributor.address}</p>}
            {distributor.city && <p className="text-sm text-[#575A57]">{distributor.city}</p>}
          </section>
        )}

        {/* Composition (liste INCO) + Allergènes — info légalement publique, déjà sur la barquette */}
        {(product.ingredients || (product.allergens && product.allergens.length > 0)) && (
          <section className="bg-white border border-[#E5E5E0] rounded-lg p-5" data-testid="trace-composition">
            <h3 className="text-lg font-semibold text-[#1A1C1A] mb-3 flex items-center gap-2">
              <Leaf className="w-5 h-5 text-[#1F4E3D]" />
              Composition
            </h3>
            {product.ingredients && (
              <div className="mb-4">
                <p className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold mb-1.5">
                  Ingrédients
                </p>
                <p className="text-sm text-[#1A1C1A] leading-relaxed" data-testid="trace-ingredients">
                  {product.ingredients}
                </p>
                <p className="text-[10px] text-[#A3A6A3] mt-2 italic">
                  Conforme au règlement UE n°1169/2011 (INCO). Les allergènes apparaissent en gras sur l&apos;étiquette de la barquette.
                </p>
              </div>
            )}
            {product.allergens && product.allergens.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold mb-1.5">
                  Allergènes déclarés
                </p>
                <div className="flex flex-wrap gap-1.5" data-testid="trace-allergens">
                  {product.allergens.map((a) => (
                    <span
                      key={a}
                      className="text-xs bg-[#FBEEEA] text-[#A04E2C] border border-[#E8B0A0] px-2 py-0.5 rounded font-medium"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Valeurs nutritionnelles + Nutri-Score */}
        {product.nutrition && (
          <section className="bg-white border border-[#E5E5E0] rounded-lg p-5" data-testid="trace-nutrition">
            <h3 className="text-lg font-semibold text-[#1A1C1A] mb-3 flex items-center gap-2">
              <Flame className="w-5 h-5 text-[#1F4E3D]" />
              Valeurs nutritionnelles
            </h3>
            {product.nutriscore && <NutriScoreBadge letter={product.nutriscore.letter} />}
            <p className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold mb-2 mt-3">
              Pour 100 g
            </p>
            <NutritionTable nutrition={product.nutrition} />
          </section>
        )}

        {/* Conservation & Réchauffage */}
        {(product.storage_instructions || product.reheating_instructions) && (
          <section className="bg-white border border-[#E5E5E0] rounded-lg p-5 space-y-4" data-testid="trace-instructions">
            <h3 className="text-lg font-semibold text-[#1A1C1A] flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#1F4E3D]" />
              Mode d&apos;emploi
            </h3>
            {product.storage_instructions && (
              <div className="flex gap-3" data-testid="trace-storage">
                <Snowflake className="w-5 h-5 text-[#3B82B5] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold mb-1">
                    Conservation
                  </p>
                  <p className="text-sm text-[#1A1C1A] leading-relaxed">{product.storage_instructions}</p>
                </div>
              </div>
            )}
            {product.reheating_instructions && (
              <div className="flex gap-3" data-testid="trace-reheating">
                <Flame className="w-5 h-5 text-[#C05C47] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold mb-1">
                    Réchauffage
                  </p>
                  <p className="text-sm text-[#1A1C1A] leading-relaxed whitespace-pre-line">{product.reheating_instructions}</p>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Contact */}
        <section className="bg-[#1F4E3D] text-white rounded-lg p-5" data-testid="trace-support">
          <h3 className="text-lg font-semibold mb-3">Question ou signalement ?</h3>
          <p className="text-sm text-[#D4A373] mb-3">
            Notre équipe est à votre écoute pour toute question sur ce produit.
          </p>
          <div className="space-y-2 text-sm">
            {support?.phone && (
              <a href={`tel:${support.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-[#D4A373]" data-testid="trace-support-phone">
                <Phone className="w-4 h-4 flex-shrink-0" /> {support.phone}
              </a>
            )}
            {support?.email && (
              <a href={`mailto:${support.email}`} className="flex items-center gap-2 hover:text-[#D4A373] break-all" data-testid="trace-support-email">
                <Mail className="w-4 h-4 flex-shrink-0" /> {support.email}
              </a>
            )}
            {support?.website && (
              <a href={support.website} className="flex items-center gap-2 hover:text-[#D4A373]" target="_blank" rel="noreferrer" data-testid="trace-support-website">
                <ExternalLink className="w-4 h-4 flex-shrink-0" /> {support.website.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        </section>

        <footer className="text-center text-[10px] text-[#A3A6A3] py-4">
          Page de traçabilité Maison Thaï · Aucun cookie, aucun tracker
        </footer>
      </main>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold mb-0.5 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </p>
      <p className="text-sm text-[#1A1C1A] font-medium">{value || "—"}</p>
    </div>
  );
}

// Badge Nutri-Score avec le code couleur officiel (A vert vif → E rouge)
function NutriScoreBadge({ letter }) {
  const palette = {
    A: { bg: "#038141", label: "Excellente qualité nutritionnelle" },
    B: { bg: "#85BB2F", label: "Bonne qualité nutritionnelle" },
    C: { bg: "#FECB02", label: "Qualité nutritionnelle moyenne" },
    D: { bg: "#EE8100", label: "Qualité nutritionnelle faible" },
    E: { bg: "#E63E11", label: "Qualité nutritionnelle à limiter" },
  };
  const p = palette[letter] || palette.C;
  const letters = ["A", "B", "C", "D", "E"];
  return (
    <div data-testid="trace-nutriscore">
      <p className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold mb-1.5">
        Nutri-Score
      </p>
      <div className="flex items-stretch rounded-md overflow-hidden border border-[#E5E5E0] w-fit">
        {letters.map((L) => {
          const isActive = L === letter;
          return (
            <span
              key={L}
              className="flex items-center justify-center font-bold text-white"
              style={{
                background: palette[L].bg,
                width: isActive ? 44 : 28,
                height: isActive ? 44 : 28,
                fontSize: isActive ? 22 : 14,
                opacity: isActive ? 1 : 0.55,
                transition: "all 0.2s",
              }}
            >
              {L}
            </span>
          );
        })}
      </div>
      <p className="text-[11px] text-[#575A57] mt-1.5">{p.label}</p>
      <p className="text-[10px] text-[#A3A6A3] mt-0.5 italic">
        Calcul indicatif selon l&apos;algorithme officiel FR/EU 2017 (Santé Publique France).
      </p>
    </div>
  );
}

// Tableau des valeurs nutritionnelles par 100 g
function NutritionTable({ nutrition }) {
  const fmt = (v, unit) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("fr-CH", { maximumFractionDigits: 1 })} ${unit}`);
  const rows = [
    {
      key: "energy",
      label: "Énergie",
      value: nutrition.energy_kj || nutrition.energy_kcal
        ? `${nutrition.energy_kj ? Math.round(nutrition.energy_kj) + " kJ" : ""}${nutrition.energy_kj && nutrition.energy_kcal ? " · " : ""}${nutrition.energy_kcal ? Math.round(nutrition.energy_kcal) + " kcal" : ""}`
        : "—",
    },
    { key: "fat", label: "Matières grasses", value: fmt(nutrition.fat, "g") },
    { key: "saturated_fat", label: "dont acides gras saturés", value: fmt(nutrition.saturated_fat, "g"), indent: true },
    { key: "carbs", label: "Glucides", value: fmt(nutrition.carbs, "g") },
    { key: "sugars", label: "dont sucres", value: fmt(nutrition.sugars, "g"), indent: true },
    { key: "proteins", label: "Protéines", value: fmt(nutrition.proteins, "g") },
    { key: "salt", label: "Sel", value: fmt(nutrition.salt, "g") },
  ];
  return (
    <table className="w-full text-sm border-collapse">
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-b border-[#F0F0EB] last:border-0">
            <td className={`py-2 text-[#575A57] ${r.indent ? "pl-4 italic" : ""}`}>{r.label}</td>
            <td className="py-2 text-right text-[#1A1C1A] font-medium tabular-nums">{r.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
